/**
 * merge_activities — surgical merge for the schedule grid's activity state.
 *
 * Problem: every full re-fetch (`GET /activities/schedule/{id}`) returns
 * fresh JSON, so every activity object has a new reference even if its
 * data is byte-for-byte identical. The grid's React reconciliation then
 * has to diff every row even though most haven't changed.
 *
 * Solution: when merging the fresh response, REUSE the prior object
 * reference for any activity whose serialisable content is unchanged.
 * React then sees `=== ` for those props and can short-circuit row
 * reconciliation (especially once Layer 7b — React.memo on the row —
 * lands).
 *
 * Two helpers:
 *   - `mergeActivitiesByWbs(prev, fresh)` — for the whole-schedule refetch
 *     pattern in UnifiedScheduleView.fetchData. `fresh` is the canonical
 *     new state grouped by wbs_id; `prev` is the previous state.
 *   - `mergeActivityList(prev, fresh)` — for the flat-list pattern used by
 *     SchedulingScreen.allActivities. Same idea, no WBS grouping.
 *
 * Both return the SAME bucket / array reference when nothing in that
 * bucket / list changed, so React.memo'd parents up the tree can also
 * short-circuit.
 *
 * Equality: shallow Object.keys + strict equals on each key. Activity
 * fields are all primitives (strings, numbers, booleans, null) or simple
 * arrays, so shallow comparison is sufficient. If a future field is an
 * object or array, the worst-case is a false negative (we treat the
 * object as changed and use the fresh ref) — correct, just less optimal.
 */

function activitiesAreEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) {
      // For arrays of primitives (e.g., resource_ids), compare contents.
      if (Array.isArray(a[k]) && Array.isArray(b[k])) {
        if (a[k].length !== b[k].length) return false;
        for (let i = 0; i < a[k].length; i++) {
          if (a[k][i] !== b[k][i]) return false;
        }
        continue;
      }
      return false;
    }
  }
  return true;
}

/**
 * Merge a fresh `{wbsId: activities[]}` map into the previous one,
 * preserving object identity for unchanged activities AND for unchanged
 * buckets. Returns the previous object reference itself if NOTHING
 * changed across the whole map.
 */
export function mergeActivitiesByWbs(prev, fresh) {
  if (!prev) return fresh;
  const result = {};
  let mapChanged = false;
  // Build a quick lookup of previous activities by id, across all buckets,
  // so we can find unchanged objects even when rows move between WBS buckets.
  const prevById = new Map();
  for (const bucket of Object.values(prev)) {
    for (const a of bucket) prevById.set(a.id, a);
  }
  for (const [wbsId, freshBucket] of Object.entries(fresh)) {
    const prevBucket = prev[wbsId];
    let bucketChanged = !prevBucket || prevBucket.length !== freshBucket.length;
    const newBucket = freshBucket.map((freshAct, i) => {
      const prevAct = prevById.get(freshAct.id);
      if (prevAct && activitiesAreEqual(prevAct, freshAct)) {
        // Unchanged — reuse the prior reference. May still flag the
        // bucket as changed if it moved positions / WBS.
        if (!bucketChanged && prevBucket[i] !== prevAct) bucketChanged = true;
        return prevAct;
      }
      bucketChanged = true;
      return freshAct;
    });
    result[wbsId] = bucketChanged ? newBucket : prevBucket;
    if (bucketChanged) mapChanged = true;
  }
  // Detect dropped buckets: present in prev, absent in fresh.
  for (const wbsId of Object.keys(prev)) {
    if (!(wbsId in fresh)) {
      mapChanged = true;
      break;
    }
  }
  return mapChanged ? result : prev;
}

/**
 * Merge a fresh flat activity list into the previous one, preserving
 * object identity for unchanged activities. Returns the previous array
 * reference if nothing changed.
 */
export function mergeActivityList(prev, fresh) {
  if (!prev) return fresh;
  if (prev.length !== fresh.length) {
    // Length differs: at least one row added/removed — must rebuild,
    // but still preserve refs for unchanged rows so downstream memo helps.
    const prevById = new Map(prev.map(a => [a.id, a]));
    return fresh.map(f => {
      const p = prevById.get(f.id);
      return p && activitiesAreEqual(p, f) ? p : f;
    });
  }
  let listChanged = false;
  const prevById = new Map(prev.map(a => [a.id, a]));
  const result = fresh.map(f => {
    const p = prevById.get(f.id);
    if (p && activitiesAreEqual(p, f)) return p;
    listChanged = true;
    return f;
  });
  // Even if every activity was reused, the order may have changed —
  // detect that by a positional check.
  if (!listChanged) {
    for (let i = 0; i < prev.length; i++) {
      if (prev[i] !== result[i]) { listChanged = true; break; }
    }
  }
  return listChanged ? result : prev;
}

// Exported for tests; keeps the one-off equality definition centralised.
export const __activitiesAreEqual = activitiesAreEqual;

/**
 * Apply a flat list of activity updates to a {wbsId: activities[]} map.
 * Used by both Layer 2 (server response merge) and Layer 5 (optimistic
 * rendering before server confirms). Goes through mergeActivitiesByWbs
 * so reference identity is preserved for unchanged buckets / rows.
 *
 * Activities in `updates` not present in `prev` are silently skipped —
 * we have no way to know which WBS to put them in. (For the Layer 2
 * cascade, every cascaded activity is already in some bucket; new
 * activities arrive via the bulk-create flow which uses the full
 * refetch path.)
 */
export function applyActivityUpdates(prev, updates) {
  if (!prev || !updates || updates.length === 0) return prev;
  const byId = new Map(updates.map(a => [a.id, a]));
  const merged = Object.fromEntries(
    Object.entries(prev).map(([wbsId, bucket]) => [
      wbsId,
      bucket.map(a => byId.has(a.id) ? { ...a, ...byId.get(a.id) } : a),
    ]),
  );
  return mergeActivitiesByWbs(prev, merged);
}

/**
 * Parse a PUT /activities/{id} or /progress response into the canonical
 * { activity, cascaded } shape.
 *
 * Layer 2 introduced a wrapped response: { activity, cascaded, cpm_ran }.
 * Existing test mocks use the legacy flat shape (an ActivityResponse
 * directly) and we may briefly run against backends that haven't been
 * deployed yet. A presence check on the wrapper keys disambiguates
 * cleanly — a flat ActivityResponse never has both `activity` and
 * `cascaded` as own keys.
 *
 * Returns { activity, cascaded: [] } where activity may be {} if the
 * mock provided nothing — callers should guard if they depend on
 * specific fields. The defensive fallback ensures the FE never throws
 * on an unexpected response shape.
 */
export function parseActivityUpdateResponse(responseData) {
  const data = responseData || {};
  const isWrapped = data && typeof data === 'object'
    && 'activity' in data && 'cascaded' in data;
  if (isWrapped) {
    return {
      activity: data.activity || {},
      cascaded: Array.isArray(data.cascaded) ? data.cascaded : [],
    };
  }
  // Legacy flat shape — treat the whole payload as the activity.
  return { activity: data, cascaded: [] };
}
